// client/api/services/rateCardService.ts
// T-15 / KX-106 — admin lifecycle for the versioned revenue split (rate cards).
//
// The split is in basis points (bps of the whole). A card is created as a DRAFT,
// submitted, then approved by a DIFFERENT admin (dual control), and only takes
// effect at effective_from (>= 30 days out). Settlement reads the card STAMPED on
// a subscription — never "the current one".
import { apiClient } from "../client";
import { ENDPOINTS } from "../endpoints";

export type RateCardStatus = "draft" | "pending_approval" | "approved" | "rejected";

export interface RateCard {
  _id?: string;
  country_code: string | null;
  label?: string | null;
  name?: string | null;
  /** Field share of the whole (the model also accepts the alias `field_bps`). */
  field_bps: number;
  company_bps: number;
  treasury_bps: number;
  /** Of the whole: acquisition + service_pool === field_bps. */
  acquisition_bps: number;
  service_pool_bps: number;
  /** Standby credit (J3): a per-day rate in bps of a reference daily value. */
  standby_bps: number;
  team_leader_bps: number;
  country_manager_bps: number;
  version?: number | null;
  status?: RateCardStatus;
  effective_from?: string | null;
  effective_to?: string | null;
  created_by?: string | null;
  submitted_by?: string | null;
  approved_by?: string | null;
  rejected_by?: string | null;
  approved_at?: string | null;
  rejection_reason?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface RateCardResponse {
  success?: boolean;
  message?: string;
  count?: number;
  data?: RateCard | RateCard[];
}

export const getRateCards = async (params?: {
  country_code?: string;
  status?: string;
}): Promise<RateCard[]> => {
  const res = await apiClient.get<RateCardResponse>(ENDPOINTS.ADMIN.RATE_CARDS, { params });
  const d = res.data?.data;
  return Array.isArray(d) ? d : [];
};

export const createRateCard = async (payload: Partial<RateCard>): Promise<RateCard | null> => {
  const res = await apiClient.post<RateCardResponse>(ENDPOINTS.ADMIN.RATE_CARDS, payload);
  const d = res.data?.data;
  return d && !Array.isArray(d) ? d : null;
};

export const updateRateCard = async (id: string, payload: Partial<RateCard>): Promise<RateCard | null> => {
  const res = await apiClient.put<RateCardResponse>(ENDPOINTS.ADMIN.RATE_CARD(id), payload);
  const d = res.data?.data;
  return d && !Array.isArray(d) ? d : null;
};

export const submitRateCard = async (id: string): Promise<RateCard | null> => {
  const res = await apiClient.post<RateCardResponse>(ENDPOINTS.ADMIN.RATE_CARD_SUBMIT(id));
  const d = res.data?.data;
  return d && !Array.isArray(d) ? d : null;
};

export const approveRateCard = async (
  id: string,
  payload?: { effective_from?: string; rationale?: string }
): Promise<RateCard | null> => {
  const res = await apiClient.post<RateCardResponse>(ENDPOINTS.ADMIN.RATE_CARD_APPROVE(id), payload || {});
  const d = res.data?.data;
  return d && !Array.isArray(d) ? d : null;
};

export const rejectRateCard = async (id: string, reason?: string): Promise<RateCard | null> => {
  const res = await apiClient.post<RateCardResponse>(ENDPOINTS.ADMIN.RATE_CARD_REJECT(id), { reason });
  const d = res.data?.data;
  return d && !Array.isArray(d) ? d : null;
};
